import { Box, Divider } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

type SplitDirection = 'horizontal' | 'vertical';

interface SplitPaneProps {
  leftPaneSize?: number;
  rightPaneSize?: number;
  children: React.ReactNode[];
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onResize?: () => void;
  paneRefs?: React.MutableRefObject<(HTMLDivElement | null)[]>;
  direction?: SplitDirection;
  reversed?: boolean;
}

const SplitPane: React.FC<SplitPaneProps> = ({
  children,
  leftPaneSize,
  rightPaneSize,
  onDragStart,
  onDragEnd,
  onResize,
  paneRefs,
  direction = 'horizontal',
  reversed = false,
}) => {
  const [sizes, setSizes] = useState<number[]>([50, 50]);

  const [isDragging, setIsDragging] = useState<boolean>(false);

  const isVertical = direction === 'vertical';

  // Reorder children if reversed
  const orderedChildren = reversed ? [...children].reverse() : children;

  useEffect(() => {
    if (leftPaneSize === rightPaneSize && sizes[0] !== 50 && sizes[1] !== 50) {
      setSizes([50, 50]);
    }
  }, [leftPaneSize, rightPaneSize]);

  const handleDragStart = (index: number) => (event: React.MouseEvent) => {
    setIsDragging(true);
    onDragStart?.();

    // Use clientY for vertical, clientX for horizontal
    let startPos = isVertical ? event.clientY : event.clientX;
    const startSizes = [...sizes];
    document.body.style.userSelect = 'none'; // Prevent text selection during drag

    // Convert the minimum size in pixels to a percentage
    const containerSize = isVertical ? window.innerHeight : window.innerWidth;
    const minSizePercentage = (2 / containerSize) * 100;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const currentPos = isVertical ? moveEvent.clientY : moveEvent.clientX;
      const delta = ((currentPos - startPos) / containerSize) * 100;

      if (index + 1 < sizes.length) {
        const newSize = startSizes[index] + delta;
        const newSizeNext = startSizes[index + 1] - delta;

        // Ensure the new sizes do not drop below the minimum threshold
        if (newSize >= minSizePercentage && newSizeNext >= minSizePercentage) {
          startSizes[index] = newSize;
          startSizes[index + 1] = newSizeNext;
          setSizes([...startSizes]); // Ensure real-time updates by spreading into a new array

          onResize?.();
        }
      }

      startPos = currentPos; // Update startPos for continuous delta calculation
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = ''; // Re-enable text selection
      setIsDragging(false);
      onDragEnd?.();
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <Box
      display="flex"
      flexDirection={isVertical ? 'column' : 'row'}
      height="100%"
      width="100%"
      overflow="hidden"
    >
      {orderedChildren.map((child, index) => {
        // Map the visual index to the original ref index when reversed
        const refIndex = reversed ? orderedChildren.length - 1 - index : index;

        return (
          <React.Fragment key={index}>
            <Box
              ref={(el: HTMLDivElement | null) => {
                if (paneRefs?.current) {
                  paneRefs.current[refIndex] = el;
                }
              }}
              flex={`0 0 ${sizes[index]}%`}
              height={isVertical ? 'auto' : '100%'}
              width={isVertical ? '100%' : 'auto'}
              className={`BDS-SplitPane-${direction}-${index}`}
              overflow="auto"
              transition={!isDragging ? 'flex 0.3s ease-out' : 'none'}
            >
              {child}
            </Box>
            {index < orderedChildren.length - 1 && (
              <Divider
                cursor={isVertical ? 'ns-resize' : 'ew-resize'}
                orientation={isVertical ? 'horizontal' : 'vertical'}
                onMouseDown={handleDragStart(index)}
                zIndex={4}
                opacity={1}
                borderLeftWidth={isVertical ? '0' : '4px'}
                borderTopWidth={isVertical ? '4px' : '100%'}
                backgroundColor={isDragging ? 'purple.400' : 'initial'}
                transition="background-color 0.2s ease-in-out"
                position="relative"
                sx={{
                  '&::before': {
                    content: '""',
                    position: 'relative',
                    top: isVertical ? '-8px' : 0,
                    left: isVertical ? 0 : '-8px',
                    right: isVertical ? 0 : '-8px',
                    bottom: isVertical ? '-8px' : 0,
                    width: isVertical ? '100%' : '20px',
                    height: isVertical ? '20px' : '100%',
                  },
                  '&:hover': {
                    backgroundColor: 'purple.400',
                  },
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default SplitPane;
